import React from 'react';
import { GovernanceColors } from '@/config/governanceColors';

interface DemoEvidence {
  step: number;
  message: string;
  kind: string;
  [key: string]: any;
}

interface DemoOverlayProps {
  message: string;
  evidence?: DemoEvidence[];
}

// Render a single evidence row showing the engine response from each completed demo step.
// Each row is a verifiable artifact (audit id, halt count, deny reason) the user can cross-check
// in the Audit Trail view after the demo completes.
function EvidenceRow({ ev }: { ev: DemoEvidence }) {
  let payload: React.ReactNode = null;
  switch (ev.kind) {
    case 'authorize-denied':
      payload = (
        <span>
          <span className="text-red-300">DENIED</span> {ev['tier'] ? `(tier=${ev['tier']})` : ''} -- {ev['reason'] || 'policy violation'}{' '}
          {ev['auditId'] ? <span className="text-purple-200 font-mono text-[10px]">audit={ev['auditId']}</span> : null}
        </span>
      );
      break;
    case 'audit-recorded':
      payload = (
        <span>
          <span className="text-green-300">RECORDED</span> -- chain has {ev['blocks'] ?? '?'} blocks, integrity {String(ev['integrity'])}
        </span>
      );
      break;
    case 'halt-fleet':
      payload = (
        <span>
          <span className="text-yellow-300">FLEET HALT</span> -- {ev['halted'] ?? 0} agents stopped{' '}
          {ev['auditId'] ? <span className="text-purple-200 font-mono text-[10px]">audit={ev['auditId']}</span> : null}
        </span>
      );
      break;
    case 'resume-fleet':
      payload = (
        <span>
          <span className="text-cyan-300">RESUMED</span> -- {ev['resumed'] ?? 'fleet'} restored from postgres + redis{' '}
          {ev['auditId'] ? <span className="text-purple-200 font-mono text-[10px]">audit={ev['auditId']}</span> : null}
        </span>
      );
      break;
    case 'demo-complete':
      payload = <span className="text-green-300">DEMO COMPLETE -- inspect Audit Trail for the 4 demo entries.</span>;
      break;
    case 'error':
      payload = <span className="text-red-300">ENGINE ERROR -- {ev['error'] || 'unknown'}</span>;
      break;
    default:
      payload = <span className="text-[#A8A29E]">{ev.message}</span>;
  }
  return (
    <div className="flex items-start gap-2 text-xs px-3 py-1.5 border-l-2 border-purple-400/50 bg-black/30">
      <span className="text-purple-300 font-mono shrink-0">[{ev.step + 1}]</span>
      <div className="flex-1">{payload}</div>
    </div>
  );
}

export function DemoOverlay({ message, evidence = [] }: DemoOverlayProps) {
  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 text-white animate-fade-in shadow-2xl" style={{ backgroundColor: `${GovernanceColors.primary}F2` }}>
      <div className="max-w-5xl mx-auto px-6 py-3">
        <div className="text-center text-base font-medium mb-2">{message}</div>
        {evidence.length > 0 && (
          <div className="space-y-1 mt-2 bg-black/40 rounded p-2 max-h-40 overflow-y-auto">
            <div className="text-[10px] uppercase tracking-wider text-purple-200 px-3 mb-1">Engine evidence (verifiable in Audit Trail)</div>
            {evidence.map((ev, i) => (
              <EvidenceRow key={i} ev={ev} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
